import { LOGIN_METHOD, type ProjectCode } from '@constants'
import { useApiOrderLoginMethods } from '@hooks-api/ec/use-api-order-login-methods'
import { type Liff } from '@line/liff'
import { Button } from '@pets-ui/Button/Button'
import { CircularProgress } from '@pets-ui/CircularProgress/CircularProgress'
import { HStack } from '@pets-ui/HStack/HStack'
import { Info } from '@pets-ui/Icons'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { setStorageItem, STORAGE_EVENT } from '@utils/storageHelper'
import { type AxiosError } from 'axios'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { getWpUserRegion } from '../../../utils/getWpUserRegion'
import { useContextCosign } from '../../useContextCosign'
import { Terms } from '../Terms'
import { SPECIAL_LOGIN_SUBSHIP_TYPE_STORAGE_KEY } from './constants'
import { ErrorMessage } from './ErrorMessage'
import { InputInternationalPhoneNumber } from './InputInternationalPhoneNumber'
import { LogoElectronicCommerce } from './Logo/LogoElectronicCommerce'
import { ThirtyPartyLogin } from './ThirtyPartyLogin'
import { useContextLogin } from './useContextLogin'

interface SpecialLoginProps {
  orderId: string
  liff?: Liff
}

const styles = stylex.create({
  title: {
    textAlign: 'center',
    marginBottom: '12px',
  },
  subtitle: {
    marginBottom: '16px',
  },
  subtitleText: {
    color: colors.grey600,
  },
  phoneRow: {
    flexWrap: 'nowrap',
  },
  phoneInputWrap: {
    flexGrow: 1,
    minWidth: 0,
  },
  phoneLoginButton: {
    flexShrink: 0,
    height: '40px',
    width: '52px',
  },
  loading: {
    padding: '32px',
    display: 'flex',
    justifyContent: 'center',
  },
})

export function SpecialLogin({ orderId, liff }: SpecialLoginProps) {
  const { t } = useTranslation()
  const { phone, setPhone, phoneError, validatePhone, countryCode, setCountryCode } = useContextLogin()
  const { customLogo, projectCode } = useContextCosign()
  const logo = customLogo ?? projectCode

  const { data, error, isLoading } = useApiOrderLoginMethods({
    order_id: orderId,
    project_code: projectCode as ProjectCode,
    country_code: getWpUserRegion(),
  })

  const subshipType = data?.data.data.subship_type

  useEffect(function bridgeSubshipTypeToSessionStorage() {
    if (!subshipType) return
    setStorageItem(SPECIAL_LOGIN_SUBSHIP_TYPE_STORAGE_KEY, { orderId, subshipType }, STORAGE_EVENT.sessionStorage)
  }, [orderId, subshipType])

  const orderLoginMethods = data?.data.data.login_methods ?? []
  const allowedMethods = orderLoginMethods.length === 0 ? undefined : orderLoginMethods
  const showSms = !allowedMethods || allowedMethods.includes(LOGIN_METHOD.SMS)
  const isOrderNotFound = (error as AxiosError | undefined)?.response?.status === 404

  return (
    <>
      <LogoElectronicCommerce logo={logo} mb={2} />
      {!error && (
        <Text variant="h4" rootStyle={styles.title}>
          {t('login.specialLoginTitle')}
        </Text>
      )}
      {isLoading && (
        <div {...stylex.props(styles.loading)}>
          <CircularProgress fontSize={40} color={colors.grey500} />
        </div>
      )}
      {error && (
        <ErrorMessage>
          {isOrderNotFound ? t('login.specialLoginOrderNotFound') : t('login.serverErrorDialogContent')}
        </ErrorMessage>
      )}
      {!isLoading && !error && (
        <>
          <HStack gap={1} alignItems="center" rootStyle={styles.subtitle}>
            <Info fontSize={20} color={colors.grey600} />
            <Text variant="caption" rootStyle={styles.subtitleText}>
              {t('login.specialLoginSubtitle', { orderId })}
            </Text>
          </HStack>
          <VStack gap={5}>
            {showSms && (
              <HStack gap={2} alignItems="flex-start" rootStyle={styles.phoneRow}>
                <div {...stylex.props(styles.phoneInputWrap)}>
                  <InputInternationalPhoneNumber
                    phone={phone}
                    setPhone={setPhone}
                    phoneError={phoneError}
                    validatePhone={validatePhone}
                    countryCode={countryCode}
                    setCountryCode={setCountryCode}
                    inputGap={8}
                  />
                </div>
                <Button
                  variant="filled"
                  onClick={validatePhone}
                  disabled={phone.trim() === ''}
                  rootStyle={styles.phoneLoginButton}
                >
                  {t('login.login')}
                </Button>
              </HStack>
            )}
            <ThirtyPartyLogin liff={liff} allowedMethods={allowedMethods} />
          </VStack>
          <Terms />
        </>
      )}
    </>
  )
}
